// frontend/src/pages/AboutPage.js
import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { 
  FaHistory, FaAward, FaHandshake, FaHeart, FaEdit, FaShieldAlt, 
  FaGem, FaTrophy, FaLeaf, FaLightbulb, FaStar, FaCamera, 
  FaSave, FaTimes, FaPlus, FaTrash, FaArrowUp, FaArrowDown
} from 'react-icons/fa';
import api from '../utils/api';
import Loader from '../components/ui/Loader';
import Message from '../components/ui/Message';

const AboutPage = () => {
  // State
  const [currentImage, setCurrentImage] = useState(0);
  const [aboutData, setAboutData] = useState({
    title: '关于我们',
    content: '',
    photos: [],
    values: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Edit state
  const [isEditing, setIsEditing] = useState(false);
  const [editedTitle, setEditedTitle] = useState('');
  const [editedContent, setEditedContent] = useState('');
  
  // Photo management state
  const [showPhotoUpload, setShowPhotoUpload] = useState(false);
  const [newPhoto, setNewPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState('');
  const [photoLoading, setPhotoLoading] = useState(false);
  
  // Values management state
  const [showValueEdit, setShowValueEdit] = useState(false);
  const [editingValue, setEditingValue] = useState(null);
  const [newValue, setNewValue] = useState({
    title: '',
    description: '',
    icon_name: 'FaHistory'
  });
  const [valueLoading, setValueLoading] = useState(false);
  
  // Refs
  const fileInputRef = useRef(null);

  // Get auth state to check if user is admin
  const { user, isAuthenticated } = useSelector(state => state.auth);
  const isAdmin = isAuthenticated && user && user.is_admin;

  // Fetch about page data
  useEffect(() => {
    const fetchAboutData = async () => {
      try {
        setLoading(true);
        setError('');
        
        const res = await api.get('/about');
        
        const data = {
          title: res.data.aboutPage?.title || '关于我们',
          content: res.data.aboutPage?.content || '',
          photos: res.data.photos || [],
          values: res.data.values || []
        };

        setAboutData(data);
        setEditedTitle(data.title);
        setEditedContent(data.content);
        
        setLoading(false);
      } catch (err) {
        console.error('Error fetching about page data:', err);
        setError('获取关于页面数据失败');
        setLoading(false);
      }
    };
    
    fetchAboutData();
  }, []);

  // Handle the case when there are fewer than desired photos by cycling
  const cyclicPhotos = aboutData.photos.length > 0 
    ? [...aboutData.photos, ...aboutData.photos, ...aboutData.photos].slice(0, Math.max(4, aboutData.photos.length)) 
    : [
        { photo_url: '/uploads/placeholder.jpg' },
        { photo_url: '/uploads/placeholder.jpg' },
        { photo_url: '/uploads/placeholder.jpg' },
        { photo_url: '/uploads/placeholder.jpg' }
      ];

  // Autorotate photos
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImage((prevImage) => (prevImage + 1) % cyclicPhotos.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [cyclicPhotos.length]);

  // Manual photo navigation
  const goToImage = (index) => {
    setCurrentImage(index);
  };

  // Icon options for company values
  const iconOptions = [
    { value: 'FaHistory', label: '历史', icon: <FaHistory /> },
    { value: 'FaAward', label: '奖项', icon: <FaAward /> },
    { value: 'FaHandshake', label: '合作', icon: <FaHandshake /> },
    { value: 'FaHeart', label: '爱心', icon: <FaHeart /> },
    { value: 'FaShieldAlt', label: '安全', icon: <FaShieldAlt /> },
    { value: 'FaGem', label: '质量', icon: <FaGem /> },
    { value: 'FaTrophy', label: '成就', icon: <FaTrophy /> },
    { value: 'FaLeaf', label: '环保', icon: <FaLeaf /> },
    { value: 'FaLightbulb', label: '创新', icon: <FaLightbulb /> },
    { value: 'FaStar', label: '卓越', icon: <FaStar /> },
  ];

  // Helper function to get the icon component based on icon name string
  const getIconComponent = (iconName) => {
    switch (iconName) {
      case 'FaHistory': return <FaHistory className="text-red-700 text-3xl" />;
      case 'FaAward': return <FaAward className="text-red-700 text-3xl" />;
      case 'FaHandshake': return <FaHandshake className="text-red-700 text-3xl" />;
      case 'FaHeart': return <FaHeart className="text-red-700 text-3xl" />;
      case 'FaShieldAlt': return <FaShieldAlt className="text-red-700 text-3xl" />;
      case 'FaGem': return <FaGem className="text-red-700 text-3xl" />;
      case 'FaTrophy': return <FaTrophy className="text-red-700 text-3xl" />;
      case 'FaLeaf': return <FaLeaf className="text-red-700 text-3xl" />;
      case 'FaLightbulb': return <FaLightbulb className="text-red-700 text-3xl" />;
      case 'FaStar': return <FaStar className="text-red-700 text-3xl" />;
      default: return <FaHistory className="text-red-700 text-3xl" />;
    }
  };

  // Default values in case API fails or doesn't return data
  const defaultValues = [
    {
      id: 1,
      title: '悠久历史',
      description: '自1995年创立至今，25年来始终坚持品质第一，不断创新发展。',
      icon_name: 'FaHistory',
    },
    {
      id: 2,
      title: '品质保证',
      description: '作为五粮液官方授权经销商，确保每一款产品的正品品质。',
      icon_name: 'FaAward',
    },
    {
      id: 3,
      title: '诚信经营',
      description: '诚信为本，以优质服务和真诚态度赢得客户的长期信任。',
      icon_name: 'FaHandshake',
    },
    {
      id: 4,
      title: '社会责任',
      description: '积极投身公益事业，支持社区建设，回馈社会。',
      icon_name: 'FaHeart',
    },
  ];

  // Use API data if available, otherwise fall back to defaults
  const displayValues = aboutData.values.length > 0 ? aboutData.values : defaultValues;

  // Handle edit mode toggle
  const toggleEditMode = () => {
    if (isEditing) {
      // If exiting edit mode, reset to original values
      setEditedTitle(aboutData.title);
      setEditedContent(aboutData.content);
    }
    setIsEditing(!isEditing);
  };

  // Save edited content
  const saveContent = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await api.put('/about', {
        title: editedTitle,
        content: editedContent
      });
      
      setAboutData({
        ...aboutData,
        title: editedTitle,
        content: editedContent
      });
      
      setSuccess('内容已成功保存');
      setTimeout(() => setSuccess(''), 3000);
      setIsEditing(false);
      setLoading(false);
    } catch (err) {
      console.error('Error saving content:', err);
      setError('保存内容失败: ' + (err.response?.data?.message || '未知错误'));
      setLoading(false);
    }
  };

  // Handle photo file selection
  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setNewPhoto(file);
      setPhotoPreview(URL.createObjectURL(file));
    }
  };

  // Upload a new photo
  const handlePhotoUpload = async () => {
    if (!newPhoto) return;
    
    try {
      setPhotoLoading(true);
      setError('');
      
      const formData = new FormData();
      // Make sure the field name 'photo' matches what the backend expects in upload.single('photo')
      formData.append('photo', newPhoto);
      
      console.log('Uploading photo...'); // Add debugging
      const res = await api.post('/about/photos', formData, {
        headers: {
          'Content-Type': 'multipart/form-data' // Ensure proper content type
        }
      });
      console.log('Upload response:', res.data); // Add debugging
      
      const updatedPhotos = [...aboutData.photos, res.data.photo];
      setAboutData({
        ...aboutData,
        photos: updatedPhotos
      });
      
      setSuccess('照片上传成功');
      setTimeout(() => setSuccess(''), 3000);
      
      setNewPhoto(null);
      setPhotoPreview('');
      setShowPhotoUpload(false);
      setPhotoLoading(false);
    } catch (err) {
      console.error('Error uploading photo:', err);
      // More detailed error logging
      if (err.response) {
        console.error('Response data:', err.response.data);
        console.error('Response status:', err.response.status);
      }
      setError('上传照片失败: ' + (err.response?.data?.message || '未知错误'));
      setPhotoLoading(false);
    }
  };

  // Delete a photo
  const handlePhotoDelete = async (id) => {
    try {
      setPhotoLoading(true);
      setError('');
      
      await api.delete(`/about/photos/${id}`);
      
      const updatedPhotos = aboutData.photos.filter(photo => photo.id !== id);
      setAboutData({
        ...aboutData,
        photos: updatedPhotos
      });
      
      setSuccess('照片删除成功');
      setTimeout(() => setSuccess(''), 3000);
      
      setPhotoLoading(false);
    } catch (err) {
      console.error('Error deleting photo:', err);
      setError('删除照片失败: ' + (err.response?.data?.message || '未知错误'));
      setPhotoLoading(false);
    }
  };

  // Move photo up or down in order
  const handlePhotoMove = async (id, direction) => {
    // Find the current photo and its index
    const photoIndex = aboutData.photos.findIndex(photo => photo.id === id);
    if (photoIndex === -1) return;
    
    // Calculate new index based on direction
    const newIndex = direction === 'up' ? photoIndex - 1 : photoIndex + 1;
    
    // Check if the new index is valid
    if (newIndex < 0 || newIndex >= aboutData.photos.length) return;
    
    // Create a copy of photos array and swap the items
    const updatedPhotos = [...aboutData.photos];
    const temp = updatedPhotos[photoIndex];
    updatedPhotos[photoIndex] = updatedPhotos[newIndex];
    updatedPhotos[newIndex] = temp;
    
    // Update the display order of each photo
    const photoOrders = updatedPhotos.map((photo, index) => ({
      id: photo.id,
      order: index + 1
    }));
    
    try {
      setPhotoLoading(true);
      setError('');
      
      await api.put('/about/photos/order', { photoOrders });
      
      // Update the order in the state
      setAboutData({
        ...aboutData,
        photos: updatedPhotos.map((photo, index) => ({
          ...photo,
          display_order: index + 1
        }))
      });
      
      setPhotoLoading(false);
    } catch (err) {
      console.error('Error reordering photos:', err);
      setError('重新排序照片失败: ' + (err.response?.data?.message || '未知错误'));
      setPhotoLoading(false);
    }
  };

  // Handle company value input change
  const handleValueChange = (field, value) => {
    setNewValue({
      ...newValue,
      [field]: value
    });
  };

  // Add new company value
  const handleAddValue = async () => {
    if (!newValue.title || !newValue.description || !newValue.icon_name) {
      setError('标题、描述和图标都是必填项');
      return;
    }
    
    try {
      setValueLoading(true);
      setError('');
      
      const res = await api.post('/about/values', newValue);
      
      const updatedValues = [...aboutData.values, res.data.value];
      setAboutData({
        ...aboutData,
        values: updatedValues
      });
      
      setNewValue({
        title: '',
        description: '',
        icon_name: 'FaHistory'
      });
      
      setSuccess('核心价值观添加成功');
      setTimeout(() => setSuccess(''), 3000);
      
      setShowValueEdit(false);
      setValueLoading(false);
    } catch (err) {
      console.error('Error adding company value:', err);
      setError('添加核心价值观失败: ' + (err.response?.data?.message || '未知错误'));
      setValueLoading(false);
    }
  };

  // Start editing a value
  const startEditValue = (value) => {
    setEditingValue({...value});
    setShowValueEdit(true);
  };

  // Handle edit change for existing value
  const handleEditValueChange = (field, value) => {
    setEditingValue({
      ...editingValue,
      [field]: value
    });
  };

  // Update existing company value
  const handleUpdateValue = async () => {
    if (!editingValue || !editingValue.title || !editingValue.description) {
      setError('标题和描述是必填项');
      return;
    }
    
    try {
      setValueLoading(true);
      setError('');
      
      await api.put(`/about/values/${editingValue.id}`, editingValue);
      
      const updatedValues = aboutData.values.map(value =>
        value.id === editingValue.id ? editingValue : value
      );
      
      setAboutData({
        ...aboutData,
        values: updatedValues
      });
      
      setSuccess('核心价值观更新成功');
      setTimeout(() => setSuccess(''), 3000);
      
      setShowValueEdit(false);
      setEditingValue(null);
      setValueLoading(false);
    } catch (err) {
      console.error('Error updating company value:', err);
      setError('更新核心价值观失败: ' + (err.response?.data?.message || '未知错误'));
      setValueLoading(false);
    }
  };

  // Delete company value
  const handleDeleteValue = async (id) => {
    try {
      setValueLoading(true);
      setError('');
      
      await api.delete(`/about/values/${id}`);
      
      const updatedValues = aboutData.values.filter(value => value.id !== id);
      setAboutData({
        ...aboutData,
        values: updatedValues
      });
      
      setSuccess('核心价值观删除成功');
      setTimeout(() => setSuccess(''), 3000);
      
      setValueLoading(false);
    } catch (err) {
      console.error('Error deleting company value:', err);
      setError('删除核心价值观失败: ' + (err.response?.data?.message || '未知错误'));
      setValueLoading(false);
    }
  };

  // Move value up or down in order
  const handleValueMove = async (id, direction) => {
    // Find the current value and its index
    const valueIndex = aboutData.values.findIndex(value => value.id === id);
    if (valueIndex === -1) return;
    
    // Calculate new index based on direction
    const newIndex = direction === 'up' ? valueIndex - 1 : valueIndex + 1;
    
    // Check if the new index is valid
    if (newIndex < 0 || newIndex >= aboutData.values.length) return;
    
    // Create a copy of values array and swap the items
    const updatedValues = [...aboutData.values];
    const temp = updatedValues[valueIndex];
    updatedValues[valueIndex] = updatedValues[newIndex];
    updatedValues[newIndex] = temp;
    
    // Update the display order of each value
    const valueOrders = updatedValues.map((value, index) => ({
      id: value.id,
      order: index + 1
    }));
    
    try {
      setValueLoading(true);
      setError('');
      
      await api.put('/about/values/order', { valueOrders });
      
      // Update the order in the state
      setAboutData({
        ...aboutData,
        values: updatedValues.map((value, index) => ({
          ...value,
          display_order: index + 1
        }))
      });
      
      setValueLoading(false);
    } catch (err) {
      console.error('Error reordering values:', err);
      setError('重新排序核心价值观失败: ' + (err.response?.data?.message || '未知错误'));
      setValueLoading(false);
    }
  };

  // Cancel value editing
  const cancelValueEdit = () => {
    setShowValueEdit(false);
    setEditingValue(null);
    setNewValue({
      title: '',
      description: '',
      icon_name: 'FaHistory'
    });
  };

  return (
    <div className="about-page">
      <div className="bg-gray-50 py-8 px-4 rounded-xl mb-8">
        <div className="container mx-auto">
          <h1 className="text-4xl font-bold mb-2">{aboutData.title}</h1>
          <div className="text-gray-600">南昌市玉洒贸易有限公司 | 五粮液官方授权店</div>
          
          {isAdmin && (
            <div className="mt-2">
              <button 
                onClick={toggleEditMode}
                className="inline-flex items-center text-blue-600 hover:text-blue-800"
              >
                {isEditing ? (
                  <>
                    <FaTimes className="mr-1" />
                    取消编辑
                  </>
                ) : (
                  <>
                    <FaEdit className="mr-1" />
                    编辑页面
                  </>
                )}
              </button>
              
              {isEditing && (
                <button 
                  onClick={saveContent}
                  className="inline-flex items-center text-green-600 hover:text-green-800 ml-4"
                >
                  <FaSave className="mr-1" />
                  保存内容
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {loading && !isEditing ? (
        <div className="flex justify-center my-12">
          <Loader />
        </div>
      ) : error ? (
        <Message variant="error" onClose={() => setError('')}>{error}</Message>
      ) : (
        <div className="container mx-auto px-4 mb-16">
          {success && (
            <Message variant="success" onClose={() => setSuccess('')}>{success}</Message>
          )}

          <div className="flex flex-col lg:flex-row gap-12">
            {/* Left side: Photos */}
            <div className="lg:w-1/2">
              <div className="relative bg-white p-4 rounded-xl shadow-md">
                {isAdmin && (
                  <div className="absolute top-2 right-2 z-10 flex space-x-2">
                    <button
                      onClick={() => setShowPhotoUpload(!showPhotoUpload)}
                      className="bg-blue-600 text-white p-2 rounded-full hover:bg-blue-700"
                      title="管理照片"
                    >
                      <FaCamera />
                    </button>
                  </div>
                )}
                
                <div className="relative overflow-hidden rounded-lg h-96">
                  {cyclicPhotos.map((photo, index) => (
                    <div
                      key={index}
                      className={`absolute inset-0 transition-opacity duration-1000 ${
                        index === currentImage ? 'opacity-100' : 'opacity-0'
                      }`}
                    >
                      <img
                        src={photo.photo_url}
                        alt={`南昌市玉洒贸易有限公司 ${index + 1}`}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          // Prevent infinite loop by only setting src once
                          if (!e.target.getAttribute('data-tried-fallback')) {
                            e.target.setAttribute('data-tried-fallback', 'true');
                            e.target.src = '/uploads/placeholder.jpg';
                          } else {
                            // If even the fallback fails, replace with a div
                            const parent = e.target.parentNode;
                            if (parent) {
                              const placeholder = document.createElement('div');
                              placeholder.className = 'w-full h-full bg-gray-200 flex items-center justify-center';
                              placeholder.innerHTML = '<span class="text-gray-500">图片不可用</span>';
                              parent.replaceChild(placeholder, e.target);
                            }
                          }
                        }}
                      />
                    </div>
                  ))}
                </div>
                
                {/* Photo navigation dots */}
                <div className="flex justify-center mt-4">
                  {cyclicPhotos.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => goToImage(index)}
                      className={`w-3 h-3 mx-1 rounded-full ${
                        index === currentImage ? 'bg-red-700' : 'bg-gray-300'
                      }`}
                      aria-label={`切换到图片 ${index + 1}`}
                    />
                  ))}
                </div>
              </div>
              
              {/* Photo Upload Section */}
              {isAdmin && showPhotoUpload && (
                <div className="mt-4 bg-white p-4 rounded-xl shadow-md">
                  <h3 className="text-lg font-medium mb-3">照片管理</h3>
                  
                  {/* Upload new photo */}
                  <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                    <h4 className="font-medium mb-2">上传新照片</h4>
                    
                    <div className="flex mb-3">
                      <div className="w-1/3 mr-3">
                        {photoPreview ? (
                          <img
                            src={photoPreview}
                            alt="Preview"
                            className="w-full h-32 object-cover rounded"
                          />
                        ) : (
                          <div className="w-full h-32 bg-gray-200 rounded flex items-center justify-center">
                            <span className="text-gray-400">照片预览</span>
                          </div>
                        )}
                      </div>
                      
                      <div className="w-2/3">
                        <input
                          type="file"
                          ref={fileInputRef}
                          onChange={handlePhotoChange}
                          className="hidden"
                          accept="image/*"
                        />
                        
                        <button
                          onClick={() => fileInputRef.current.click()}
                          className="w-full py-2 px-3 border border-gray-300 rounded text-sm text-center hover:bg-gray-50"
                        >
                          <FaCamera className="inline mr-1" />
                          选择照片
                        </button>
                        
                        <button
                          onClick={handlePhotoUpload}
                          disabled={!newPhoto || photoLoading}
                          className={`w-full mt-2 py-2 px-3 rounded text-sm text-center ${
                            !newPhoto || photoLoading
                              ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                              : 'bg-blue-600 text-white hover:bg-blue-700'
                          }`}
                        >
                          {photoLoading ? <Loader size="sm" /> : '上传照片'}
                        </button>
                      </div>
                    </div>
                  </div>
                  
                  {/* Photo list */}
                  <h4 className="font-medium mb-2">当前照片</h4>
                  
                  {aboutData.photos.length === 0 ? (
                    <p className="text-gray-500 text-sm">暂无照片</p>
                  ) : (
                    <div className="space-y-2">
                      {aboutData.photos.map((photo, index) => (
                        <div key={photo.id} className="flex items-center p-2 bg-gray-50 rounded">
                          <img
                            src={photo.photo_url}
                            alt={`照片 ${index + 1}`}
                            className="w-12 h-12 object-cover rounded mr-3"
                          />
                          <div className="flex-grow text-sm text-gray-500">
                            显示顺序: {index + 1}
                          </div>
                          <div className="flex space-x-1">
                            <button
                              onClick={() => handlePhotoMove(photo.id, 'up')}
                              disabled={index === 0 || photoLoading}
                              className={`p-1 rounded ${
                                index === 0 || photoLoading
                                  ? 'text-gray-300 cursor-not-allowed'
                                  : 'text-blue-600 hover:text-blue-800'
                              }`}
                            >
                              <FaArrowUp size={14} />
                            </button>
                            <button
                              onClick={() => handlePhotoMove(photo.id, 'down')}
                              disabled={index === aboutData.photos.length - 1 || photoLoading}
                              className={`p-1 rounded ${
                                index === aboutData.photos.length - 1 || photoLoading
                                  ? 'text-gray-300 cursor-not-allowed'
                                  : 'text-blue-600 hover:text-blue-800'
                              }`}
                            >
                              <FaArrowDown size={14} />
                            </button>
                            <button
                              onClick={() => handlePhotoDelete(photo.id)}
                              disabled={photoLoading}
                              className={`p-1 rounded ${
                                photoLoading
                                  ? 'text-gray-300 cursor-not-allowed'
                                  : 'text-red-600 hover:text-red-800'
                              }`}
                            >
                              <FaTrash size={14} />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Right side: Company Info */}
            <div className="lg:w-1/2">
              <div className="bg-white p-6 rounded-xl shadow-md">
                <h2 className="text-2xl font-bold mb-6 text-red-800">
                  {isEditing ? (
                    <input
                      type="text"
                      value={editedTitle}
                      onChange={(e) => setEditedTitle(e.target.value)}
                      className="w-full p-2 border rounded"
                    />
                  ) : (
                    aboutData.title
                  )}
                </h2>
                
                <div className="space-y-6 text-gray-700">
                  {isEditing ? (
                    <textarea
                      value={editedContent}
                      onChange={(e) => setEditedContent(e.target.value)}
                      className="w-full p-2 border rounded min-h-[300px]"
                      rows={10}
                    />
                  ) : aboutData.content ? (
                    aboutData.content.split('\n\n').map((paragraph, idx) => (
                      <p key={idx} className="leading-relaxed">
                        {paragraph}
                      </p>
                    ))
                  ) : (
                    <p className="text-gray-500 italic">暂无内容</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Company Core Values Section */}
          <div className="mt-16">
            <div className="flex justify-between items-center mb-12">
              <h2 className="text-3xl font-bold">我们的核心价值观</h2>
              
              {isAdmin && (
                <button
                  onClick={() => {
                    setEditingValue(null);
                    setShowValueEdit(true);
                  }}
                  className="inline-flex items-center text-green-600 hover:text-green-800"
                >
                  <FaPlus className="mr-1" />
                  添加价值观
                </button>
              )}
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {displayValues.map((value, index) => (
                <div key={value.id} className="bg-white p-6 rounded-xl shadow-md text-center hover:shadow-lg transition-shadow relative">
                  {isAdmin && (
                    <div className="absolute top-2 right-2 z-10 flex space-x-1">
                      <button
                        onClick={() => handleValueMove(value.id, 'up')}
                        disabled={index === 0 || valueLoading}
                        className={`p-1 rounded-full ${
                          index === 0 || valueLoading
                            ? 'text-gray-300 cursor-not-allowed'
                            : 'text-blue-600 hover:bg-blue-50'
                        }`}
                      >
                        <FaArrowUp size={12} />
                      </button>
                      <button
                        onClick={() => handleValueMove(value.id, 'down')}
                        disabled={index === displayValues.length - 1 || valueLoading}
                        className={`p-1 rounded-full ${
                          index === displayValues.length - 1 || valueLoading
                            ? 'text-gray-300 cursor-not-allowed'
                            : 'text-blue-600 hover:bg-blue-50'
                        }`}
                      >
                        <FaArrowDown size={12} />
                      </button>
                      <button
                        onClick={() => startEditValue(value)}
                        className="p-1 text-blue-600 hover:bg-blue-50 rounded-full"
                      >
                        <FaEdit size={12} />
                      </button>
                      <button
                        onClick={() => handleDeleteValue(value.id)}
                        className="p-1 text-red-600 hover:bg-red-50 rounded-full"
                      >
                        <FaTrash size={12} />
                      </button>
                    </div>
                  )}
                  <div className="bg-red-100 w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-4">
                    {getIconComponent(value.icon_name)}
                  </div>
                  <h3 className="text-xl font-bold mb-3">{value.title}</h3>
                  <p className="text-gray-600">
                    {value.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Edit Value Modal */}
      {showValueEdit && (
        <div className="fixed inset-0 z-50 overflow-auto bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold">
                  {editingValue ? '编辑核心价值观' : '添加核心价值观'}
                </h3>
                <button
                  onClick={cancelValueEdit}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <FaTimes />
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">标题</label>
                  <input
                    type="text"
                    value={editingValue ? editingValue.title : newValue.title}
                    onChange={(e) => {
                      if (editingValue) {
                        handleEditValueChange('title', e.target.value);
                      } else {
                        handleValueChange('title', e.target.value);
                      }
                    }}
                    className="w-full p-2 border rounded"
                    placeholder="例如: 品质保证"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">描述</label>
                  <textarea
                    value={editingValue ? editingValue.description : newValue.description}
                    onChange={(e) => {
                      if (editingValue) {
                        handleEditValueChange('description', e.target.value);
                      } else {
                        handleValueChange('description', e.target.value);
                      }
                    }}
                    className="w-full p-2 border rounded"
                    rows={3}
                    placeholder="描述该核心价值观..."
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">图标</label>
                  <div className="grid grid-cols-5 gap-2">
                    {iconOptions.map(option => (
                      <div 
                        key={option.value}
                        onClick={() => {
                          if (editingValue) {
                            handleEditValueChange('icon_name', option.value);
                          } else {
                            handleValueChange('icon_name', option.value);
                          }
                        }}
                        className={`flex flex-col items-center justify-center p-2 border rounded cursor-pointer ${
                          (editingValue ? editingValue.icon_name : newValue.icon_name) === option.value
                            ? 'bg-blue-50 border-blue-500'
                            : 'hover:bg-gray-50'
                        }`}
                      >
                        <div className="text-xl mb-1">{option.icon}</div>
                        <div className="text-xs text-gray-500">{option.label}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={cancelValueEdit}
                  className="px-4 py-2 border rounded text-gray-600 hover:bg-gray-50"
                >
                  取消
                </button>
                
                <button
                  type="button"
                  onClick={editingValue ? handleUpdateValue : handleAddValue}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                  disabled={valueLoading}
                >
                  {valueLoading ? (
                    <Loader size="sm" />
                  ) : editingValue ? (
                    '保存修改'
                  ) : (
                    '添加'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AboutPage;